<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        {{ isDoctorContext ? 'Add Doctor Education Detail' : 'Add Education Detail' }}
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        {{ isDoctorContext 
                          ? 'Add a new educational qualification for the selected doctor'
                          : 'Add a new educational qualification and certification details'
                        }}
                      </p>
                      <v-chip 
                        v-if="isDoctorContext && usersList.length > 0"
                        color="success" 
                        outlined 
                        x-small 
                        class="mt-2"
                      >
                        <v-icon left x-small>mdi-doctor</v-icon>
                        For: {{ usersList[0].display_name }}
                      </v-chip>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-school</v-icon>
                      New Education Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="goBack"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                <!-- Educational Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-school</v-icon>
                    Educational Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="usersList"
                        item-value="id"
                        item-text="display_name"
                        label="User"
                        placeholder="Select user"
                        outlined
                        :rules="[(v) => !!v || 'User is required']"
                        :error-messages="formErrors.user_id"
                        hide-details="auto"
                        required
                        :disabled="!canSelectUser || isDoctorContext"
                        clearable
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.qualification"
                        label="Qualification"
                        placeholder="e.g., MBBS, MD, BAMS, PhD"
                        outlined
                        :rules="[(v) => !!v || 'Qualification is required']"
                        :error-messages="formErrors.qualification"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-certificate</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.university"
                        label="University/College"
                        placeholder="Name of university or college"
                        outlined
                        :error-messages="formErrors.university"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-school</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.specialization"
                        label="Specialization"
                        placeholder="e.g., Cardiology, Ayurveda, General Medicine"
                        outlined
                        :error-messages="formErrors.specialization"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-medical-bag</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.year_of_passing"
                        label="Year of Passing"
                        placeholder="e.g., 2020"
                        outlined
                        type="number"
                        :min="1950"
                        :max="currentYear + 5"
                        :rules="yearRules"
                        :error-messages="formErrors.year_of_passing"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="form.is_active"
                        label="Active Status"
                        color="primary"
                        hide-details="auto"
                        class="mt-2"
                      ></v-switch>
                    </v-col>
                  </v-row>
                </div>

                <!-- Certificate Upload Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2"
                      >mdi-file-document</v-icon
                    >
                    Certificate Upload
                  </h3>

                  <v-row>
                    <v-col cols="12">
                      <v-file-input
                        v-model="certificateFile"
                        label="Certificate/Degree Document"
                        placeholder="Upload certificate (PDF, JPG, PNG)"
                        outlined
                        accept="application/pdf,image/jpeg,image/png,image/jpg"
                        :rules="fileRules"
                        :error-messages="formErrors.certificate_file"
                        class="professional-field"
                        hide-details="auto"
                        prepend-icon=""
                        @change="handleFileUpload"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-file-upload</v-icon>
                        </template>
                      </v-file-input>
                      <div class="text-caption grey--text mt-1">
                        Supported formats: PDF, JPG, PNG. Maximum size: 5MB
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Form Actions -->
                <div class="form-actions mt-4">
                  <v-row>
                    <v-col cols="12">
                      <v-divider class="mb-4"></v-divider>
                      <div class="d-flex justify-end align-center">
                        <v-btn
                          color="grey"
                          outlined
                          @click="resetForm"
                          class="mr-3"
                          :disabled="loading"
                        >
                          <v-icon left>mdi-refresh</v-icon>
                          Reset Form
                        </v-btn>
                        <v-btn
                          color="grey darken-1"
                          outlined
                          @click="goBack"
                          class="mr-3"
                          :disabled="loading"
                        >
                          <v-icon left>mdi-close</v-icon>
                          Cancel
                        </v-btn>
                        <v-btn
                          color="primary"
                          @click="saveEducationDetail"
                          :loading="loading"
                          :disabled="!valid || loading"
                        >
                          <v-icon left>mdi-content-save</v-icon>
                          Save Education Detail
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "EducationDetailCreate",

  data() {
    return {
      valid: false,
      loading: false,
      certificateFile: null,
      usersList: [],
      currentYear: new Date().getFullYear(),

      form: {
        user_id: null,
        qualification: "",
        university: "",
        specialization: "",
        year_of_passing: null,
        company_id: null, // Will be set in mounted()
        is_active: true, // Ensure it's explicitly boolean
      },

      formErrors: {}, // Renamed to avoid conflict with global mixin

      yearRules: [
        (v) => {
          if (!v) return true; // Optional field
          const year = parseInt(v);
          if (year < 1950) return "Year cannot be before 1950";
          if (year > this.currentYear + 5)
            return `Year cannot be more than ${this.currentYear + 5}`;
          return true;
        },
      ],

      fileRules: [
        (v) => {
          if (!v) return true; // Optional field
          if (v.size > 5 * 1024 * 1024)
            return "File size must be less than 5MB";
          const allowedTypes = [
            "application/pdf",
            "image/jpeg",
            "image/png",
            "image/jpg",
          ];
          if (!allowedTypes.includes(v.type))
            return "Only PDF, JPG, and PNG files are allowed";
          return true;
        },
      ],
    };
  },

  computed: {
    canSelectUser() {
      // Only admins can select different users, regular users can only create for themselves
      return this.roleName && ["SUPER ADMIN", "ADMIN"].includes(this.roleName);
    },

    isDoctorContext() {
      // Check if we're creating education details for a specific doctor
      return (
        this.$route.name === "DoctorEducationDetailCreatePage" &&
        !!this.$route.params.doctorId
      );
    },

    doctorId() {
      return this.isDoctorContext ? this.$route.params.doctorId : null;
    },
  },

  watch: {
    // Watch is no longer needed as we handle user_id setting directly in loadUsers
  },

  async mounted() {
    // Set company_id from store (standard pattern)
    if (this.company && this.company.id) {
      this.form.company_id = this.company.id;
    }
    
    await this.loadUsers();
    this.initializeForm();
  },

  methods: {
    async loadUsers() {
      try {
        if (this.isDoctorContext) {
          // The doctorId from route params is already encrypted
          const encryptedDoctorId = this.doctorId;
          console.log("Loading doctor with encrypted ID:", encryptedDoctorId);
          
          const response = await axios.get(`/users/${encryptedDoctorId}`);
          console.log("Doctor API response:", response.data);
          
          if (response.data.success || response.data.data) {
            const doctor = response.data.data || response.data;
            const displayName = this.formatUserDisplayName(doctor);
            
            this.usersList = [{
              id: doctor.id,
              display_name: displayName,
            }];
            
            console.log("Doctor loaded successfully:", { 
              doctor, 
              displayName, 
              usersList: this.usersList,
              formUserId: this.form.user_id 
            });
            
            // Immediately set the form user_id to the doctor's ID
            this.form.user_id = doctor.id;
          }
        } else {
          const response = await axios.get("/users", {
            params: { rowsPerPage: -1 },
          });

          if (response.data.success || response.data.data) {
            const users = response.data.data || response.data;
            this.usersList = users.map((user) => ({
              id: user.id,
              display_name: this.formatUserDisplayName(user),
            }));
          }
        }
      } catch (error) {
        console.error("Error loading users:", error);
        this.$toast.error("Failed to load user information");
      }
    },

    formatUserDisplayName(user) {
      const firstName = user.first_name || "";
      const lastName = user.last_name || "";
      const userName = user.user_name || "";
      const email = user.email || "";

      // Build full name from first and last name
      const fullName = `${firstName} ${lastName}`.trim();
      
      // Use email as identifier if username is same as full name
      const identifier = (userName !== fullName) ? userName : email;

      // Return formatted name
      if (fullName && identifier) {
        return `${fullName} (${identifier})`;
      } else if (fullName) {
        return fullName;
      } else if (userName) {
        return userName;
      } else if (email) {
        return email;
      } else {
        return `User #${user.id}`;
      }
    },

    initializeForm() {
      if (!this.canSelectUser && this.user) {
        this.form.user_id = this.user.id;
      }

      // Doctor context initialization will be handled after loadUsers() completes
      // since we need the doctor data to be loaded first
    },

    handleFileUpload(file) {
      this.certificateFile = file;
      if (file && this.formErrors.certificate_file) {
        this.formErrors.certificate_file = null;
      }
    },

    async saveEducationDetail() {
      // Validate form
      if (!this.$refs.form.validate()) {
        this.$store.dispatch(
          "errors/setError",
          "Please fill in all required fields correctly"
        );
        return;
      }

      try {
        this.loading = true;
        this.formErrors = {};

        // Create FormData for file upload
        const formData = new FormData();

        // Append form fields with proper formatting
        Object.keys(this.form).forEach((key) => {
          if (this.form[key] !== null && this.form[key] !== "") {
            // Convert boolean to string for FormData
            if (typeof this.form[key] === 'boolean') {
              formData.append(key, this.form[key] ? '1' : '0');
            } else {
              formData.append(key, this.form[key]);
            }
          }
        });

        // Ensure company_id is included (required by backend)
        if (!formData.has('company_id') && this.company && this.company.id) {
          formData.append('company_id', this.company.id);
        }

        // Append certificate file if uploaded
        if (this.certificateFile) {
          formData.append("certificate_file", this.certificateFile);
        }

        // Debug: Log the form data being sent
        console.log("Form data being sent:");
        for (let pair of formData.entries()) {
          console.log(pair[0] + ': ' + pair[1]);
        }

        const response = await axios.post("/education_details", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data.success) {
          this.$store.dispatch(
            "success/setSuccess",
            response.data.message || "Education detail created successfully"
          );
          this.$router.back();
        } else {
          this.$store.dispatch(
            "errors/setError",
            response.data.message || "Failed to create education detail"
          );
        }
      } catch (error) {
        console.error("Error saving education detail:", error);

        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          this.formErrors = error.response.data.errors;
          this.$store.dispatch(
            "errors/setError",
            "Please check the form for errors"
          );
        } else {
          this.$store.dispatch(
            "errors/setError",
            "Error saving education detail"
          );
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      let userId = null;
      
      if (this.isDoctorContext && this.usersList.length > 0) {
        // Use the loaded doctor's ID
        userId = this.usersList[0].id;
      } else if (!this.canSelectUser && this.user) {
        // Use current user's ID for non-admin users
        userId = this.user.id;
      }
      
      this.form = {
        user_id: userId,
        qualification: "",
        university: "",
        specialization: "",
        year_of_passing: null,
        company_id: this.company && this.company.id ? this.company.id : null,
        is_active: true, // Ensure it's explicitly boolean
      };
      this.certificateFile = null;
      this.formErrors = {};
      this.$refs.form.resetValidation();
    },

    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style scoped>
.form-card {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.page-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 12px 12px 0 0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0;
}

.page-subtitle {
  font-size: 0.875rem;
  margin-top: 4px;
}

.form-section {
  background: #fafbfc;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #1976d2;
}

.section-title {
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.professional-field .v-input__control .v-input__slot {
  border-radius: 8px;
}

.professional-field .v-text-field__details {
  padding-left: 12px;
}

.form-actions {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
}

.back-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-header .v-row {
    flex-direction: column;
  }

  .page-header .v-col {
    text-align: center;
    margin-bottom: 8px;
  }

  .form-section {
    padding: 16px;
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 1rem;
  }
}
</style>